import { useState, useEffect, useCallback } from "react";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { formatDateTime } from "../Common/DateFormat";
import CustomSnackbar from "../Common/snackBar";
import renderStatusBadge from "../../utils/badges";
import { getMailLogsAction } from "../../Actions/MailLogsAction";
import DataTable from "../../components/DataTable/DataTable"

const getDefaultDateRange = () => {
	const end = new Date();
	const start = new Date();
	start.setMonth(start.getMonth() - 1);

	return { from: start, to: end };
};

const MailLogsSection = () => {
	const [logsData, setLogsData] = useState([]);
	const defaultRange = getDefaultDateRange();

	const [filterValues, setFilterValues] = useState({
		status: "all",
		module: "all",
		dateRange: defaultRange,
	});
	const [selectedRows, setSelectedRows] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
	const [totalRecords, setTotalRecords] = useState(0);

	const [loading, setLoading] = useState(true);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	const startDate = filterValues.dateRange?.from
		? new Date(
			Date.UTC(
				filterValues.dateRange.from.getFullYear(),
				filterValues.dateRange.from.getMonth(),
				filterValues.dateRange.from.getDate(),
				0,
				0,
				0,
				0
			)
		).toISOString()
		: null;

	const endDate = filterValues.dateRange?.to
		? new Date(
			Date.UTC(
				filterValues.dateRange.to.getFullYear(),
				filterValues.dateRange.to.getMonth(),
				filterValues.dateRange.to.getDate(),
				23,
				59,
				59,
				999
			)
		).toISOString()
		: null;

	const fetchMailLogs = useCallback(
		async (isFilterAction = false) => {
			setLoading(true);
			try {
				const res = await getMailLogsAction(
					currentPage,
					rowsPerPage,
					filterValues.status,
					filterValues.module,
					startDate,
					endDate
				);

				setLogsData(res.data.data.rows);
				setTotalRecords(res.data.data.count);
				setSelectedRows([]);
			} catch {
				setSnackbar({
					open: true,
					message: "Failed to fetch mail logs",
					severity: "error",
				});
			} finally {
				setLoading(false);
			}
		}, [
		currentPage,
		rowsPerPage,
		filterValues
	]
	);

	useEffect(() => {
		fetchMailLogs();
	}, [currentPage, rowsPerPage, filterValues]);

	const handleFilterApply = (values) => {
		setCurrentPage(1);
		setFilterValues(values);
	};

	const handleFilterReset = () => {
		setCurrentPage(1);

		setFilterValues({
			status: "all",
			module: "all",
			dateRange: defaultRange,
		});
	};
	const columns = [
		// {
		// 	field: "select",
		// 	minWidth: 50,
		// 	renderHeader: () => (
		// 		<Checkbox
		// 			checked={
		// 				logsData.length > 0 &&
		// 				selectedRows.length === logsData.length
		// 			}
		// 			onChange={(e) =>
		// 				setSelectedRows(
		// 					e.target.checked ? logsData.map((r) => r.id) : []
		// 				)
		// 			}
		// 			sx={MyCheckboxIcon}
		// 		/>
		// 	),
		// 	renderCell: ({ row }) => (
		// 		<Checkbox
		// 			checked={selectedRows.includes(p.row.id)}
		// 			onChange={() =>
		// 				setSelectedRows((prev) =>
		// 					prev.includes(p.row.id)
		// 						? prev.filter((i) => i !== p.row.id)
		// 						: [...prev, p.row.id]
		// 				)
		// 			}
		// 			sx={MyCheckboxIcon}
		// 		/>
		// 	),
		// },
		{ field: "from", headerName: "From", flex: 1, minWidth: 150 },
		{ field: "to", headerName: "To", flex: 1, minWidth: 150 },
		{ field: "subject", headerName: "Subject", flex: 1, minWidth: 150 },
		{
			field: "status",
			headerName: "Status",
			renderCell: ({ row }) =>
				renderStatusBadge(
					{
						0: { title: "Pending", status: "warning" },
						1: { title: "Sent", status: "success" },
						2: { title: "Failed", status: "danger" },
					}[row.status]
				),
		},
		{
			field: "createdAt",
			headerName: "Date And Time",
			minWidth: 200,
			renderCell: ({ row }) => formatDateTime(row.createdAt, true),
		},
	];

	const filters = [
		{
			name: "status",
			label: "Status",
			type: "select",
			options: [
				{ label: "All Status", value: "all" },
				{ label: "Pending", value: "0" },
				{ label: "Sent", value: "1" },
				{ label: "Failed", value: "2" },
			],
		},
		{
			name: "module",
			label: "Module",
			type: "select",
			options: [
				{ label: "All Modules", value: "all" },
				{ label: "Users", value: "users" },
				{ label: "Workflow", value: "workflow" },
				{
					label: "Support Ticket",
					value: "support_ticket",
				},
			],
		},
		{
			name: "dateRange",
			label: "Date Range",
			type: "dateRange",
		},
	];

	return (
		<>
			<div id="kt_content_container">
				<BreadcrumbsComponent />

				<DataTable
					rows={logsData}
					columns={columns}
					loading={loading}
					totalCount={totalRecords}
					page={currentPage}
					pageSize={rowsPerPage}
					getRowId={(row) => row.id}
					filters={filters}
					filterValues={filterValues}
					onFilterApply={handleFilterApply}
					onFilterReset={handleFilterReset}
					onPageChange={setCurrentPage}
					onPageSizeChange={(size) => {
						setRowsPerPage(size);
						setCurrentPage(1);
					}}
				/>
				<CustomSnackbar
					{...snackbar}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
				/>
			</div>
		</>
	);
};

export default MailLogsSection;
